/**
 * Game State Saver
 * 
 * Handles saving and loading mid-game state to localStorage.
 * Enables players to resume games after app closure, task switching, or crashes.
 */

import { BaseGameMode, GameState } from '../game/core/BaseGameMode'
import { GameStats } from '../game/core/GameCore'
import { CellType, CellState } from '../game/CellTypes'
import { Cell } from '../game/CellTypes'
import { PairConfiguration } from '../game/FallingPair'

export interface SavedCell {
    type: CellType
    state: CellState
    x: number
    y: number
    markedForDeletion: boolean
}

export interface SavedPair {
    primaryType: CellType
    secondaryType: CellType
    pivotX: number
    pivotY: number
    rotation: number
    fallTimer: number
    fallSpeed: number
}

export interface SavedGameState {
    // Metadata
    version: string
    savedAt: number
    gameMode: string

    // Grid state - 6x12 grid (height x width format for easier serialization)
    grid: (SavedCell | null)[][]

    // Current falling pair
    currentPair: SavedPair | null

    // Next pairs queue
    nextPairs: PairConfiguration[]

    // Game statistics
    stats: GameStats

    // Timing information
    gameStartTime: number
    pausedTime: number
    totalPausedDuration: number

    // Game state
    gameState: GameState
}

export class GameStateSaver {
    private static readonly STORAGE_KEY = 'sct-game-saved-state'
    private static readonly VERSION = '1.0'
    private static readonly MAX_SAVE_AGE_MS = 7 * 24 * 60 * 60 * 1000 // 7 days

    /**
     * Save the current game state
     */
    public static saveGame(gameMode: BaseGameMode, modeName: string = 'Endless'): boolean {
        try {
            const currentPair = gameMode.getCurrentPair()
            const grid = gameMode.getGrid()
            const stats = gameMode.getStats()

            // Only save if the game is actually in progress
            const currentState = gameMode.getState()
            if (currentState === GameState.WAITING_TO_START ||
                currentState === GameState.GAME_OVER ||
                currentState === GameState.VICTORY) {
                console.log('GameStateSaver: Not saving - game not in saveable state:', currentState)
                return false
            }

            const savedState: SavedGameState = {
                version: this.VERSION,
                savedAt: Date.now(),
                gameMode: modeName,

                // Serialize grid (6 columns x 12 rows)
                grid: this.serializeGrid(grid),

                // Serialize current falling pair
                currentPair: this.serializePair(currentPair),

                // Copy next pairs queue
                nextPairs: this.getNextPairs(gameMode),

                // Copy current stats
                stats: { ...stats },

                // Save timing information
                gameStartTime: (gameMode as any).gameStartTime || Date.now(),
                pausedTime: Date.now(), // Save current time as pause time
                totalPausedDuration: (gameMode as any).totalPausedDuration || 0,

                // Save current game state
                gameState: currentState
            }

            localStorage.setItem(this.STORAGE_KEY, JSON.stringify(savedState))
            console.log('GameStateSaver: Game state saved successfully at', new Date().toLocaleTimeString())
            return true
        } catch (error) {
            console.error('GameStateSaver: Failed to save game state:', error)
            return false
        }
    }

    /**
     * Load saved game state
     */
    public static loadGame(): SavedGameState | null {
        try {
            const saved = localStorage.getItem(this.STORAGE_KEY)
            if (!saved) return null

            const state: SavedGameState = JSON.parse(saved)

            // Validate version
            if (state.version !== this.VERSION) {
                console.warn('GameStateSaver: Saved game version mismatch, clearing save')
                this.clearSave()
                return null
            }

            // Check if save is too old
            const age = Date.now() - state.savedAt
            if (age > this.MAX_SAVE_AGE_MS) {
                console.warn('GameStateSaver: Saved game is too old, clearing save')
                this.clearSave()
                return null
            }

            // Validate critical data
            if (!state.grid || !Array.isArray(state.grid) || state.grid.length !== 12) {
                console.warn('GameStateSaver: Invalid grid data in save file')
                this.clearSave()
                return null
            }

            console.log('GameStateSaver: Loaded save from', new Date(state.savedAt).toLocaleString())
            return state
        } catch (error) {
            console.error('GameStateSaver: Failed to load game state:', error)
            this.clearSave() // Clear corrupted save
            return null
        }
    }

    /**
     * Check if there's a valid saved game
     */
    public static hasSavedGame(): boolean {
        const savedState = this.loadGame()
        return savedState !== null
    }

    /**
     * Clear saved game
     */
    public static clearSave(): void {
        try {
            localStorage.removeItem(this.STORAGE_KEY)
            console.log('GameStateSaver: Saved game cleared')
        } catch (error) {
            console.error('GameStateSaver: Failed to clear saved game:', error)
        }
    }

    /**
     * Get save info without loading full state
     */
    public static getSaveInfo(): { savedAt: number; gameMode: string } | null {
        try {
            const saved = localStorage.getItem(this.STORAGE_KEY)
            if (!saved) return null

            const state = JSON.parse(saved)
            return {
                savedAt: state.savedAt,
                gameMode: state.gameMode || 'Unknown'
            }
        } catch {
            return null
        }
    }

    /**
     * Serialize grid to simple format
     */
    private static serializeGrid(grid: any): (SavedCell | null)[][] {
        const result: (SavedCell | null)[][] = []

        // Grid is 6 columns x 12 rows
        for (let y = 0; y < 12; y++) {
            result[y] = []
            for (let x = 0; x < 6; x++) {
                const cell = grid.getCell(x, y)
                if (cell) {
                    result[y][x] = {
                        type: cell.type,
                        state: cell.state,
                        x: cell.x,
                        y: cell.y,
                        markedForDeletion: cell.markedForDeletion || false
                    }
                } else {
                    result[y][x] = null
                }
            }
        }

        return result
    }

    /**
     * Serialize falling pair
     */
    private static serializePair(pair: any): SavedPair | null {
        if (!pair) return null

        try {
            const cells = pair.getCells()
            const state = pair.getState()

            return {
                primaryType: cells.primary.type,
                secondaryType: cells.secondary.type,
                pivotX: state.x,
                pivotY: state.y,
                rotation: state.rotation,
                fallTimer: pair.fallTimer || 0,
                fallSpeed: pair.fallSpeed || 1000
            }
        } catch (error) {
            console.warn('GameStateSaver: Failed to serialize falling pair:', error)
            return null
        }
    }

    /**
     * Get next pairs from game mode
     */
    private static getNextPairs(gameMode: BaseGameMode): PairConfiguration[] {
        try {
            // Try to get next pairs using the public method
            if (typeof (gameMode as any).getNextPairs === 'function') {
                return (gameMode as any).getNextPairs() || []
            }

            // Fallback to empty array if method doesn't exist
            return []
        } catch (error) {
            console.warn('GameStateSaver: Failed to get next pairs:', error)
            return []
        }
    }

    /**
     * Clear any existing saved game
     */
    static clearSavedGame(): void {
        try {
            localStorage.removeItem(this.STORAGE_KEY)
            console.log('GameStateSaver: Saved game cleared')
        } catch (error) {
            console.error('GameStateSaver: Failed to clear saved game:', error)
        }
    }
}